import type { Meter } from '@tunasong/models'
import { meterBottom, meterTop } from '@tunasong/models'
import { displayAccidentals } from '@tunasong/music-lib'
import type { Entity, Scale, ScaleType, Song } from '@tunasong/schemas'
import { tempoLabel } from '@tunasong/ui-layouts'

/**
 * How a song's key reads on a card (Bowie, 2026-10-17). Pentatonic and harmonic or melodic minor describe melodic
 * material, so the key is the parent major or minor; church modes keep their (capitalised) name.
 */
const keyQuality: Record<ScaleType, string> = {
  major: 'major',
  ionian: 'major',
  majorpentatonic: 'major',
  minor: 'minor',
  aeolian: 'minor',
  harmonicminor: 'minor',
  melodicminor: 'minor',
  minorpentatonic: 'minor',
  dorian: 'Dorian',
  phrygian: 'Phrygian',
  lydian: 'Lydian',
  mixolydian: 'Mixolydian',
  locrian: 'Locrian',
}

/** "D major", "E♭ minor", "D Dorian"; a key with no type never claims a quality: "Key of D" */
const keyText = (scale: Scale) => {
  const root = displayAccidentals(scale.root)
  return scale.type ? `${root} ${keyQuality[scale.type]}` : `Key of ${root}`
}

const isCompound = (meter: Meter) => meterBottom(meter) === 8 && [6, 9, 12].includes(meterTop(meter))

/**
 * Quarter-note BPM, except in a compound meter, where musicians count the dotted quarter: next to "6/8" a raw
 * "68 BPM" reads 1.5× too fast, so it shows "♩.=45" as the Performance header does.
 */
const tempoText = (tempo: number, meter: Meter | undefined) =>
  meter && isCompound(meter) ? tempoLabel(tempo, meter) : `${tempo} BPM`

/** 4/4 goes without saying; a compound meter always shows, since its ♩. tempo needs it alongside */
const meterText = (meter: Meter) =>
  meterTop(meter) === 4 && meterBottom(meter) === 4 ? undefined : `${meterTop(meter)}/${meterBottom(meter)}`

const pad = (n: number) => String(n).padStart(2, '0')

/** "4:12", "1:02:05" */
const durationText = (seconds: number) => {
  const total = Math.round(seconds)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const secs = total % 60
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(secs)}` : `${minutes}:${pad(secs)}`
}

const songLineParts = (song: Song): string[] => {
  const { scale, rhythm, duration, coverSource } = song
  const artist = coverSource?.originalArtist.trim()
  const { tempo, meter } = rhythm ?? {}
  return [
    artist ? `Cover of ${artist}` : undefined,
    scale ? keyText(scale) : undefined,
    tempo ? tempoText(tempo, meter) : undefined,
    meter ? meterText(meter) : undefined,
    duration ? durationText(duration) : undefined,
  ].filter((part): part is string => Boolean(part))
}

/**
 * The segments of a song card's music line: cover artist, key, tempo, meter and length, from the song's own fields.
 * Missing fields are left out; a song with none of them, and anything that is not a song, has none.
 */
export const songMusicLineParts = (entity: Entity): string[] =>
  entity.type === 'song' ? songLineParts(entity as Song) : []
